import { Box, Paper, Typography, Skeleton } from '@mui/material';
import { providerEngagementRequest } from '@uplift/api';
import { providerScheduleViewTypeRoute, ROUTING_CONFIG } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import { CalendarEditView } from '@uplift/types';
import { Button } from '@uplift/ui-v2';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTodaySessionsStyles } from '../styles';

type Structure = {
  description: React.ReactNode;
  titleOverride?: string;
  centerContent?: true;
  buttons?: {
    updateAvailability?: true;
    updateProfile?: true;
  };
};

const availabilityLink = providerScheduleViewTypeRoute.createLink({}, {
  editView: CalendarEditView.recurringHours
});

export const BookNewClients: React.FC = () => {
  const l10n = useL10n();
  const { classes } = useTodaySessionsStyles();
  const { data, isLoading } = useQuery({
    queryKey: [ 'provider-engagement' ],
    queryFn: async () => await providerEngagementRequest(null)
  });

  const structure = useMemo((): Structure => {
    if (!data) return null;
    const { hasIdealCaseload, hasAtLeastFiveSpecialties, hasEnoughHours } = data;


    if (!hasIdealCaseload) {
      if (!hasAtLeastFiveSpecialties && !hasEnoughHours) {
        return {
          description: (
            <>
              <Typography color="textSecondary">{l10n.sessions.bookNewClients.tryTheseTips}</Typography>
              <ul className={classes.list}>
                <li><Typography>{l10n.sessions.bookNewClients.availabilityTip}</Typography></li>
                <li><Typography>{l10n.sessions.bookNewClients.specialtiesTip}</Typography></li>
              </ul>
            </>
          ),
          buttons: {
            updateAvailability: true,
            updateProfile: true,
          }
        };
      }
      if (!hasAtLeastFiveSpecialties) {
        return {
          description: l10n.sessions.bookNewClients.specialtiesDescription,
          buttons: {
            updateProfile: true,
          }
        };
      }
      if (!hasEnoughHours) {
        return {
          description: l10n.sessions.bookNewClients.availabilityDescription,
          buttons: {
            updateAvailability: true
          }
        };
      }
    }
    return {
      titleOverride: l10n.sessions.bookNewClients.yourScheduleIsOpen,
      description: l10n.sessions.bookNewClients.noUpcomingSessions,
      centerContent: true
    };
  }, [ classes.list, data, l10n.sessions.bookNewClients ]);

  if (structure === null || isLoading) {
    return <Skeleton variant="rectangular" height="200px" width="100%" />;
  }

  return (
    <Paper className={classes.paperContainer}>
      <Box className={classes.paperContent} textAlign={structure.centerContent ? 'center' : undefined}>
        <Box className={classes.contentStack}>
          <Typography variant="h6" color="textSecondary">{structure.titleOverride ?? l10n.sessions.bookNewClients.title}</Typography>
          {typeof structure.description === 'string'
            ? <Typography color="textSecondary">{structure.description}</Typography>
            : structure.description}
        </Box>
        {structure.buttons && (
          <Box className={classes.bookNewClientsButtonContainer} mt={3}>
            {structure.buttons.updateAvailability && (
              <Button
                variant="outlined"
                fullWidth
                color="secondary"
                component={Link}
                to={availabilityLink}
              >
                {l10n.sessions.bookNewClients.updateAvailability}
              </Button>
            )}
            {structure.buttons.updateProfile && (
              <Button
                variant="outlined"
                fullWidth
                color="secondary"
                component={Link}
                to={ROUTING_CONFIG.profile}
              >
                {l10n.sessions.bookNewClients.updateProfile}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};
